import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, BellOff, LogOut, Package, ListOrdered, Settings as SettingsIcon,
  Printer, Check, X, Truck, Clock, MapPin, Phone, Mail, Loader2,
  Plus, Pencil, Trash2, RefreshCcw, Volume2, Copy, ChevronDown, ChevronUp,
  CalendarClock, Tag, Percent, Sun, Moon, FileBarChart, Download,
} from "lucide-react";
import { useWakeLock } from "@/hooks/useWakeLock";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge, { STATUS_LABELS } from "@/components/StatusBadge";
import { printOrderToStar, buildTestXml, sendToStarPrinter } from "@/lib/starPrint";

const POLL_MS = 8000;

const STATUS_FLOW_DELIVERY = ["en_attente", "en_cours", "en_route", "termine"];
const STATUS_FLOW_PICKUP = ["en_attente", "en_cours", "termine"];
const getFlow = (orderType) => (orderType === "pickup" ? STATUS_FLOW_PICKUP : STATUS_FLOW_DELIVERY);

const fmtCHF = (n) => `${Number(n || 0).toFixed(2)} CHF`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return iso;
  }
};
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

// ---------- STRIDENT BELL (Web Audio API) ----------
// Plays a loud, repetitive restaurant-bell pattern that loops every ~2s
// until stopBell() is called. Falls back gracefully if AudioContext missing.
const useBell = () => {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);
  const [active, setActive] = useState(false);

  const ensureCtx = () => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  };

  const ring = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    const now = ctx.currentTime;

    // LOUD bell-like pattern (max gain ~1.0 — heaviest within safe range)
    const playTone = (freq, start, dur = 0.45, gain = 1.0) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "square"; // square = louder/sharper than triangle
      osc.frequency.setValueAtTime(freq, start);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(gain, start + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + dur + 0.05);
    };

    // Stack 2 oscillators per beat for richer/louder sound (more reliable on mobile)
    const beat = (start, baseFreq, dur = 0.45) => {
      playTone(baseFreq, start, dur, 1.0);
      playTone(baseFreq * 1.5, start, dur, 0.6); // 5th overtone — perceived volume boost
    };

    // Strident pattern: 5 rapid loud hits
    beat(now, 1760, 0.35);
    beat(now + 0.16, 2349, 0.35);
    beat(now + 0.36, 1760, 0.4);
    beat(now + 0.56, 2349, 0.35);
    beat(now + 0.82, 1760, 0.6);

    // Vibrate on mobile devices to grab attention even when phone is muted/headphones off
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate([180, 80, 180, 80, 320]);
      }
    } catch (e) { /* noop */ }
  }, []);

  const startBell = useCallback(() => {
    if (intervalRef.current) return;
    setActive(true);
    ring();
    intervalRef.current = setInterval(ring, 2000);
  }, [ring]);

  const stopBell = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setActive(false);
  }, []);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (ctxRef.current) ctxRef.current.close().catch(() => {});
  }, []);

  return { startBell, stopBell, bellActive: active };
};

// ---------- RECEIPT PRINT (80mm) ----------
const escapeHtml = (s = "") => String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const buildReceiptHtml = (order) => {
  const itemsHtml = order.items.map((it) => {
    const addons = (it.selected_addons || [])
      .map((a) => `<div class="ad">+ ${escapeHtml(a.name)}${Number(a.price) > 0 ? ` (+${Number(a.price).toFixed(2)})` : ""}</div>`)
      .join("");
    const comment = it.comment
      ? `<div class="note"><b>NOTE :</b> ${escapeHtml(it.comment)}</div>`
      : "";
    const lineTotal = ((it.line_total != null ? it.line_total : (it.price + (it.addons_total || 0)) * it.quantity)).toFixed(2);
    return `
      <tr>
        <td class="it-name">
          <div class="b">${it.quantity}x ${escapeHtml(it.name)}</div>
          ${addons}
          ${comment}
        </td>
        <td class="it-price">${lineTotal}</td>
      </tr>
    `;
  }).join("");

  return `<!doctype html>
<html><head><meta charset="utf-8" /><title>Ticket #${order.order_number}</title>
<style>
  /* Thermal printer optimised: works for both 58mm and 80mm rolls */
  @page { size: 80mm auto; margin: 0; }
  @media print {
    html, body { width: 80mm; }
    .no-print { display: none !important; }
  }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; background:#fff; color:#000; }
  body { width: 80mm; padding: 3mm 4mm; font-family: 'Courier New', 'Liberation Mono', monospace; font-size: 12px; line-height: 1.4; font-weight: 600; }
  h1 { font-size: 22px; text-align:center; margin: 0 0 4px; letter-spacing: 2px; font-weight: 900; }
  .center { text-align: center; }
  .b { font-weight: 800; }
  .big { font-size: 16px; font-weight: 900; }
  .xl { font-size: 20px; font-weight: 900; }
  hr { border: none; border-top: 2px dashed #000; margin: 6px 0; }
  table { width: 100%; border-collapse: collapse; }
  .row { display:flex; justify-content:space-between; gap: 6px; }
  .small { font-size: 10px; font-weight: 600; }
  .badge { display:inline-block; border:2px solid #000; padding:2px 6px; font-weight:900; letter-spacing:1px; }
  .warn { background:#000; color:#fff; padding:4px 6px; font-weight:900; text-align:center; letter-spacing:1px; }
  .it-name { padding:4px 0; vertical-align: top; }
  .it-price { text-align:right; padding:4px 0; white-space:nowrap; vertical-align:top; font-weight:800; }
  .ad { font-size: 10px; padding-left: 8px; font-weight: 600; }
  .note { font-size: 12px; padding:3px 5px; margin:3px 0 5px 8px; border:2px solid #000; background:#eee; font-weight:700; }
  .toolbar { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .btn { font: inherit; background:#000; color:#fff; padding:8px 14px; border:0; border-radius: 6px; cursor:pointer; font-weight: 800; }
  .btn.alt { background:#fff; color:#000; border:2px solid #000; }
</style></head>
<body>
  <div class="toolbar no-print">
    <button class="btn" onclick="window.print()">Imprimer</button>
    <button class="btn alt" onclick="window.close()">Fermer</button>
  </div>
  <h1>QUANG</h1>
  <div class="center small">Restaurant Vietnamien · Lausanne</div>
  <div class="center small">Rue Pré-du-Marché 15 · 1004</div>
  <div class="center small b">Tel : 078 616 29 29</div>
  <hr />
  <div class="row"><span class="b">Ticket</span><span class="xl">#${order.order_number}</span></div>
  <div class="row"><span>Date</span><span>${fmtDate(order.created_at)}</span></div>
  <div class="row"><span>Type</span><span class="badge">${order.order_type === "delivery" ? "LIVRAISON" : "À EMPORTER"}</span></div>
  ${order.is_next_day ? `<div class="warn">⚠ POUR DEMAIN · ${escapeHtml(order.scheduled_date || "")}${order.scheduled_time && order.scheduled_time !== "asap" ? " · " + escapeHtml(order.scheduled_time) : ""}</div>` : (order.scheduled_time && order.scheduled_time !== "asap" ? `<div class="row"><span>Créneau</span><span class="b big">${escapeHtml(order.scheduled_time)}</span></div>` : "")}
  <hr />
  <div class="b">CLIENT</div>
  <div class="b">${escapeHtml(order.first_name)} ${escapeHtml(order.last_name)}</div>
  <div class="small">${escapeHtml(order.phone || "")}</div>
  <div class="small">${escapeHtml(order.email || "")}</div>
  ${order.order_type === "delivery" ? `
    <hr />
    <div class="b">LIVRAISON</div>
    <div class="b">${escapeHtml(order.address || "")}</div>
    <div class="b">${escapeHtml(order.postal_code || "")}</div>
  ` : ""}
  ${order.note ? `<hr /><div class="b">NOTE COMMANDE</div><div class="note">${escapeHtml(order.note)}</div>` : ""}
  <hr />
  <table>${itemsHtml}</table>
  <hr />
  <div class="row"><span>Sous-total</span><span>${(order.items_subtotal != null ? order.items_subtotal : order.subtotal).toFixed(2)}</span></div>
  ${Number(order.discount_total || 0) > 0 ? `<div class="row b"><span>Remise${(order.applied_promotions || []).length > 0 ? " · " + escapeHtml((order.applied_promotions || []).join(", ")) : ""}</span><span>-${Number(order.discount_total).toFixed(2)}</span></div>` : ""}
  ${order.order_type === "delivery" ? `<div class="row"><span>Livraison</span><span>${Number(order.delivery_fee || 0).toFixed(2)}</span></div>` : ""}
  <hr />
  <div class="row xl"><span>TOTAL</span><span>${order.total.toFixed(2)} CHF</span></div>
  <hr />
  <div class="center small">Paiement sur place / à la livraison</div>
  <div class="center small">Merci et à bientôt !</div>
  <div class="center b" style="margin-top:6px;">— QUANG —</div>
  <div style="height: 20mm;"></div>
  <script>
    // Try to auto-trigger print right after load. Many browsers honor this in a popup.
    window.addEventListener('load', function () {
      setTimeout(function () { try { window.print(); } catch(e) {} }, 250);
    });
    window.addEventListener('afterprint', function () { /* keep window open so user can re-print */ });
  </script>
</body></html>`;
};

const printReceipt = async (order, settings) => {
  // 1) If a Star WebPRNT printer is configured & enabled → send directly to it
  //    (bypasses the OS print dialog completely — no more A4 prompt on tablets).
  if (settings && settings.printer_enabled && settings.printer_ip) {
    const res = await printOrderToStar(order, settings);
    if (res.ok) {
      toast.success("Ticket envoyé à l'imprimante");
      return;
    }
    toast.error("Imprimante: " + res.message);
    // Don't fall back to A4 dialog automatically — user explicitly chose direct printing.
    return;
  }
  // 2) Fallback — HTML preview window with browser print dialog (legacy behavior)
  const html = buildReceiptHtml(order);
  const win = window.open("", "_blank", "width=420,height=720");
  if (!win) {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
};

const printReceiptInline = async (order, settings) => {
  // Auto-print path (after status change). Prefer Star WebPRNT when configured.
  if (settings && settings.printer_enabled && settings.printer_ip) {
    const res = await printOrderToStar(order, settings);
    if (res.ok) return;
    // silent failure on auto-print, user can retry from the order card
    console.warn("Star auto-print failed:", res.message);
    return;
  }
  // Inline iframe printing — fallback when no Star printer is configured.
  const html = buildReceiptHtml(order);
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("data-testid", "auto-print-iframe");
  document.body.appendChild(iframe);
  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) { /* noop */ }
    setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) { /* noop */ } }, 60_000);
  };
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
};

// ---------- PAGE ----------
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [bellMuted, setBellMuted] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, today_orders: 0, today_revenue: 0, total_customers: 0 });
  const [loading, setLoading] = useState(true);

  const { startBell, stopBell, bellActive } = useBell();
  const seenIdsRef = useRef(new Set());

  // ---- WAKE LOCK (keeps screen on for kitchen tablets/phones) ----
  const wakeLockInitial = (() => {
    if (typeof localStorage === "undefined") return true;
    const v = localStorage.getItem("quang_admin_wakelock");
    return v === null ? true : v === "1";
  })();
  const { supported: wakeLockSupported, enabled: wakeLockEnabled, active: wakeLockActive, setEnabled: setWakeLockEnabled } =
    useWakeLock(wakeLockInitial);
  useEffect(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("quang_admin_wakelock", wakeLockEnabled ? "1" : "0");
    }
  }, [wakeLockEnabled]);

  // ---- AUTH GUARD ----
  useEffect(() => {
    const token = localStorage.getItem("quang_admin_token");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  // ---- LOAD INITIAL ----
  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
      return data;
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("quang_admin_token");
        navigate("/admin/login");
      }
      return [];
    }
  }, [navigate]);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (e) { /* noop */ }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await loadOrders();
      // Mark already-existing pending orders as "seen" on first load (don't trigger bell on mount)
      data.filter((o) => o.status === "en_attente").forEach((o) => seenIdsRef.current.add(o.id));
      await Promise.all([loadProducts(), loadSettings(), loadStats()]);
      setLoading(false);
    })();
  }, [loadOrders, loadProducts, loadSettings, loadStats]);

  // ---- POLLING ----
  useEffect(() => {
    const i = setInterval(async () => {
      await loadOrders();
      await loadStats();
    }, POLL_MS);
    return () => clearInterval(i);
  }, [loadOrders, loadStats]);

  // ---- BELL TRIGGER ----
  // Ring whenever a new pending order arrives (and there are pending orders).
  // Stop when no pending orders remain.
  useEffect(() => {
    const pending = orders.filter((o) => o.status === "en_attente");
    let newOnesDetected = false;
    pending.forEach((o) => {
      if (!seenIdsRef.current.has(o.id)) {
        seenIdsRef.current.add(o.id);
        newOnesDetected = true;
      }
    });

    if (pending.length === 0) {
      stopBell();
    } else if (newOnesDetected && !bellMuted && audioUnlocked) {
      startBell();
    }
  }, [orders, bellMuted, audioUnlocked, startBell, stopBell]);

  // ---- ACTIONS ----
  const setStatus = async (order, status) => {
    try {
      await api.put(`/admin/orders/${order.id}/status`, { status });
      toast.success(`Commande #${order.order_number} → ${STATUS_LABELS[status]}`);
      stopBell();
      // Note: ticket d'impression est mis en file CloudPRNT côté backend
      // dès la transition en_attente → en_cours. L'imprimante Star mC-Print2
      // récupère le ticket automatiquement par polling (pas d'action côté navigateur).
      await loadOrders();
      await loadStats();
    } catch (e) {
      toast.error("Erreur mise à jour");
    }
  };

  const reprintOrder = async (order) => {
    try {
      await api.post(`/admin/orders/${order.id}/reprint`);
      toast.success(`Ticket #${order.order_number} renvoyé à l'imprimante`);
      await loadOrders();
    } catch (e) {
      toast.error("Erreur de réimpression");
    }
  };

  const deleteOrder = async (order) => {
    if (!window.confirm(`Supprimer la commande #${order.order_number} ?\nCette action est irréversible mais la commande reste comptabilisée dans les chiffres.`)) {
      return;
    }
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Commande #${order.order_number} supprimée`);
      await loadOrders();
      await loadStats();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur de suppression";
      toast.error(msg);
    }
  };

  const logout = () => {
    localStorage.removeItem("quang_admin_token");
    localStorage.removeItem("quang_device_token");
    navigate("/admin/login");
  };

  const unlockAudio = useCallback(() => {
    // Browser autoplay policy: AudioContext requires a user gesture.
    // Play a silent ping on the SHARED context (in useBell) so the unlock
    // persists for subsequent ring() calls — not just a throwaway context.
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      try {
        // Trigger one silent tone via the bell hook to instantiate + resume the shared ctx
        const ctx = new AC();
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        osc.connect(g).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.05);
        ctx.close();
      } catch (e) { /* noop */ }
    }
    // Also test vibration permission on mobile so it's pre-warmed
    try {
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
        navigator.vibrate(30);
      }
    } catch (e) { /* noop */ }
    // Request fullscreen (must be inside this user-gesture handler)
    try {
      const el = document.documentElement;
      const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
      if (req && !document.fullscreenElement && !document.webkitFullscreenElement) {
        req.call(el).catch(() => { /* user denied or unsupported */ });
      }
    } catch (e) { /* noop */ }
    // Force wake lock ON (will trigger acquire via the hook's useEffect)
    setWakeLockEnabled(true);
    setAudioUnlocked(true);
  }, [setWakeLockEnabled]);

  const pendingCount = useMemo(() => orders.filter((o) => o.status === "en_attente").length, [orders]);

  // ---------- RENDER ----------
  return (
    <div className="min-h-screen bg-[#F4F4F2] text-neutral-900">
      {/* FULL-SCREEN AUDIO UNLOCK OVERLAY (mobile autoplay policy) */}
      {!audioUnlocked && (
        <div
          onClick={unlockAudio}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") unlockAudio(); }}
          data-testid="audio-unlock-overlay"
          className="fixed inset-0 z-[100] bg-[#0A0A0A]/95 backdrop-blur-sm flex flex-col items-center justify-center px-6 cursor-pointer select-none"
        >
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#D90429] flex items-center justify-center mb-6 animate-pulse shadow-[0_0_60px_rgba(217,4,41,0.5)]">
            <Volume2 className="w-12 h-12 sm:w-14 sm:h-14 text-white" />
          </div>
          <h1 className="text-white text-3xl sm:text-4xl font-black tracking-tight text-center mb-3">
            Toucher pour activer les alertes
          </h1>
          <p className="text-neutral-400 text-sm sm:text-base text-center max-w-md mb-8">
            Pour que la cloche sonne à chaque nouvelle commande, votre navigateur exige une première interaction.
          </p>
          <div className="bg-[#E9C46A] text-black font-black uppercase tracking-wider text-sm sm:text-base px-8 py-4 rounded-md shadow-lg">
            Activer le son maintenant
          </div>
          <p className="text-neutral-600 text-xs uppercase tracking-[0.2em] mt-8">
            Quang · Admin
          </p>
        </div>
      )}

      {/* TOP BAR */}
      <div className="bg-[#0A0A0A] text-white border-b border-neutral-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-[#D90429] flex items-center justify-center font-black text-sm sm:text-base flex-shrink-0">Q</div>
            <div className="leading-none min-w-0">
              <div className="font-black text-sm sm:text-base tracking-tight truncate">Quang · Admin</div>
              <div className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5">Gestion restaurant</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            {!audioUnlocked ? (
              <Button
                onClick={unlockAudio}
                data-testid="enable-audio-btn"
                className="bg-[#E9C46A] text-black hover:bg-[#d9b45a] rounded-md h-9 px-2 sm:px-3 text-xs font-bold uppercase tracking-wider"
              >
                <Volume2 className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Activer le son</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => { setBellMuted((m) => !m); if (!bellMuted) stopBell(); }}
                data-testid="mute-bell-btn"
                className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800 rounded-md h-9 px-2 sm:px-3 text-xs font-bold uppercase tracking-wider"
              >
                {bellMuted ? <BellOff className="w-4 h-4 sm:mr-1.5" /> : <Bell className={`w-4 h-4 sm:mr-1.5 ${bellActive ? "animate-pulse text-[#D90429]" : ""}`} />}
                <span className="hidden sm:inline">{bellMuted ? "Muet" : bellActive ? "Sonne…" : "Cloche"}</span>
              </Button>
            )}
            {wakeLockSupported && (
              <Button
                variant="outline"
                onClick={() => setWakeLockEnabled((v) => !v)}
                data-testid="wakelock-toggle-btn"
                title={wakeLockEnabled ? "Écran maintenu allumé" : "Écran peut s'éteindre"}
                className={`bg-transparent border-neutral-700 hover:bg-neutral-800 rounded-md h-9 px-2 sm:px-3 text-xs font-bold uppercase tracking-wider ${
                  wakeLockEnabled ? "text-[#E9C46A] border-[#E9C46A]/50" : "text-white"
                }`}
              >
                {wakeLockEnabled ? (
                  <Sun className={`w-4 h-4 sm:mr-1.5 ${wakeLockActive ? "" : "opacity-60"}`} />
                ) : (
                  <Moon className="w-4 h-4 sm:mr-1.5" />
                )}
                <span className="hidden sm:inline">{wakeLockEnabled ? "Veille off" : "Veille on"}</span>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="bg-transparent border-neutral-700 text-white hover:bg-neutral-800 rounded-md h-9 px-2 sm:px-3 text-xs font-bold uppercase tracking-wider"
            >
              <LogOut className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          <StatCard label="En attente" value={stats.pending} accent="#D90429" testid="stat-pending" pulse={stats.pending > 0} />
          <StatCard label="En préparation" value={stats.in_progress} accent="#0A0A0A" testid="stat-in-progress" />
          <StatCard label="Commandes aujourd'hui" value={stats.today_orders} accent="#2A9D8F" testid="stat-today-orders" />
          <StatCard label="Recette du jour" value={fmtCHF(stats.today_revenue)} accent="#E9C46A" testid="stat-revenue" />
        </div>
      </div>

      {/* TABS */}
      <div className="max-w-7xl mx-auto px-3 sm:px-5 md:px-8 mt-6 sm:mt-8">
        <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto no-scrollbar -mx-3 sm:mx-0 px-3 sm:px-0">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders" icon={ListOrdered}>
            Commandes
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-[#D90429] text-white text-[10px] font-bold w-5 h-5 rounded-full">
                {pendingCount}
              </span>
            )}
          </TabBtn>
          <TabBtn active={tab === "products"} onClick={() => setTab("products")} testid="tab-products" icon={Package}>Produits</TabBtn>
          <TabBtn active={tab === "promotions"} onClick={() => setTab("promotions")} testid="tab-promotions" icon={Percent}>Promotions</TabBtn>
          <TabBtn active={tab === "hours"} onClick={() => setTab("hours")} testid="tab-hours" icon={CalendarClock}>Horaires</TabBtn>
          <TabBtn active={tab === "chiffres"} onClick={() => setTab("chiffres")} testid="tab-chiffres" icon={FileBarChart}>Chiffres</TabBtn>
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testid="tab-settings" icon={SettingsIcon}>Paramètres</TabBtn>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-neutral-400" /></div>
        ) : (
          <>
            {tab === "orders" && (
              <OrdersPanel
                orders={orders}
                onSetStatus={setStatus}
                onPrint={(o) => printReceipt(o, settings)}
                onReprint={reprintOrder}
                onDelete={deleteOrder}
                onRefresh={loadOrders}
                audioUnlocked={audioUnlocked}
              />
            )}
            {tab === "products" && (
              <ProductsPanel
                products={products}
                reload={loadProducts}
                settings={settings}
                onSettingsSaved={loadSettings}
              />
            )}
            {tab === "promotions" && (
              <PromotionsPanel
                products={products}
              />
            )}
            {tab === "hours" && settings && (
              <HoursPanel
                settings={settings}
                onSaved={loadSettings}
              />
            )}
            {tab === "chiffres" && (
              <AccountingPanel />
            )}
            {tab === "settings" && settings && (
              <SettingsPanel
                settings={settings}
                onSaved={loadSettings}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============== SUB-COMPONENTS ==============

const StatCard = ({ label, value, accent, testid, pulse }) => (
  <div
    data-testid={testid}
    className="relative bg-white border border-neutral-200 rounded-lg p-4 md:p-5 overflow-hidden"
  >
    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">{label}</p>
    <p className={`text-2xl md:text-3xl font-black tracking-tight mt-2 ${pulse ? "animate-pulse" : ""}`} style={{ color: accent }}>
      {value}
    </p>
  </div>
);

const TabBtn = ({ active, onClick, testid, icon: Icon, children }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2.5 sm:py-3 text-[11px] sm:text-sm font-bold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
      active ? "border-[#D90429] text-[#D90429]" : "border-transparent text-neutral-500 hover:text-neutral-900"
    }`}
  >
    <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
    {children}
  </button>
);

// ---------- ORDERS PANEL ----------
const OrdersPanel = ({ orders, onSetStatus, onPrint, onReprint, onDelete, onRefresh, audioUnlocked }) => {
  const [filter, setFilter] = useState("active"); // active | all | en_attente | en_cours | en_route | termine | annule

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    if (filter === "active") return orders.filter((o) => ["en_attente", "en_cours", "en_route"].includes(o.status));
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  return (
    <div>
      {!audioUnlocked && (
        <div className="bg-[#E9C46A]/20 border border-[#E9C46A] text-neutral-900 rounded-md p-3 mb-5 text-sm flex items-center gap-2" data-testid="audio-warning">
          <Volume2 className="w-4 h-4" />
          Le son s&apos;activera automatiquement au premier clic. Si vous préférez, cliquez sur <strong className="mx-1">Activer le son</strong> en haut à droite.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap gap-2">
          {[
            { v: "active", l: "En cours" },
            { v: "en_attente", l: "En attente" },
            { v: "en_cours", l: "En préparation" },
            { v: "en_route", l: "En route" },
            { v: "termine", l: "Terminées" },
            { v: "annule", l: "Annulées" },
            { v: "all", l: "Toutes" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 rounded-md border ${
                filter === f.v
                  ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                  : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
        <Button onClick={onRefresh} variant="outline" data-testid="refresh-orders-btn" className="h-9 rounded-md border-neutral-300">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Rafraîchir
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center text-neutral-500" data-testid="empty-orders">
          Aucune commande pour ce filtre.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((o) => (
            <OrderCard key={o.id} order={o} onSetStatus={onSetStatus} onPrint={onPrint} onReprint={onReprint} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

const OrderCard = ({ order, onSetStatus, onPrint, onReprint, onDelete }) => {
  const isPending = order.status === "en_attente";
  const flow = getFlow(order.order_type);
  const curIdx = flow.indexOf(order.status);
  const nextStatus = curIdx >= 0 ? flow[curIdx + 1] : null;
  return (
    <div
      data-testid={`order-card-${order.order_number}`}
      className={`bg-white border rounded-xl overflow-hidden ${
        isPending ? "border-[#D90429] shadow-lg ring-2 ring-[#D90429]/20 animate-pulse-once" : "border-neutral-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-neutral-100 bg-neutral-50 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-neutral-500">Commande</span>
          <span className="font-black text-base sm:text-lg tracking-tight">#{order.order_number}</span>
          {order.is_next_day && (
            <span
              data-testid={`order-next-day-${order.order_number}`}
              className="text-[10px] uppercase tracking-wider font-black bg-[#E9C46A] text-black px-2 py-0.5 rounded"
            >
              Demain · {order.scheduled_date || ""}{order.scheduled_time && order.scheduled_time !== "asap" ? ` · ${order.scheduled_time}` : ""}
            </span>
          )}
        </div>
        <StatusBadge status={order.status} data-testid={`order-${order.order_number}-status`} />
      </div>

      {/* Print status badge (CloudPRNT) */}
      {order.print_status && order.print_status !== "none" && (
        <div className="px-5 pt-2 -mb-1 flex items-center gap-2 flex-wrap">
          {order.print_status === "queued" && (
            <span
              data-testid={`order-${order.order_number}-print-queued`}
              className="text-[10px] uppercase tracking-wider font-black bg-[#E9C46A] text-black px-2 py-0.5 rounded inline-flex items-center gap-1"
            >
              <Printer className="w-3 h-3" /> En attente d&apos;impression
            </span>
          )}
          {order.print_status === "printed" && (
            <span
              data-testid={`order-${order.order_number}-print-printed`}
              className="text-[10px] uppercase tracking-wider font-black bg-[#2A9D8F]/15 text-[#2A9D8F] border border-[#2A9D8F]/30 px-2 py-0.5 rounded inline-flex items-center gap-1"
            >
              <Check className="w-3 h-3" /> Imprimé
            </span>
          )}
          {Number(order.reprint_count || 0) > 0 && (
            <span
              data-testid={`order-${order.order_number}-reprint-count`}
              className="text-[10px] uppercase tracking-wider font-bold text-neutral-500"
            >
              ×{order.reprint_count} réimpression{Number(order.reprint_count) > 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      <div className="px-4 sm:px-5 py-4 space-y-3">
        {/* Times: received + requested — highlighted for kitchen */}
        <div
          data-testid={`order-times-${order.order_number}`}
          className="flex flex-wrap items-stretch gap-2"
        >
          <div className="flex-1 min-w-[130px] bg-neutral-100 border border-neutral-200 rounded-md px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.15em] font-black text-neutral-500">Reçue</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Clock className="w-3.5 h-3.5 text-neutral-700" />
              <span
                data-testid={`order-received-time-${order.order_number}`}
                className="font-black text-base tabular-nums"
              >
                {fmtTime(order.created_at)}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-[130px] bg-[#D90429]/10 border-2 border-[#D90429] rounded-md px-3 py-2">
            <div className="text-[9px] uppercase tracking-[0.15em] font-black text-[#D90429]">
              {order.order_type === "delivery" ? "Livrer à" : "Prêt à"}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Clock className="w-3.5 h-3.5 text-[#D90429]" />
              <span
                data-testid={`order-requested-time-${order.order_number}`}
                className="font-black text-base tabular-nums text-[#D90429]"
              >
                {order.scheduled_time && order.scheduled_time !== "asap"
                  ? order.scheduled_time
                  : "Dès que possible"}
              </span>
              {order.is_next_day && order.scheduled_date && (
                <span className="text-[10px] font-bold text-[#D90429] uppercase tracking-wider">
                  · {order.scheduled_date}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            {order.order_type === "delivery" ? <Truck className="w-4 h-4 text-[#D90429]" /> : <Package className="w-4 h-4 text-[#0A0A0A]" />}
            <span className="font-bold uppercase tracking-wider text-xs">
              {order.order_type === "delivery" ? "Livraison" : "À emporter"}
            </span>
            <span className="text-neutral-400">·</span>
            <span className="text-neutral-500 text-xs">~{order.estimated_minutes} min</span>
          </div>
          <div className="text-right">
            <span className="font-black text-lg">{fmtCHF(order.total)}</span>
            {Number(order.discount_total || 0) > 0 && (
              <div
                data-testid={`order-discount-${order.order_number}`}
                className="text-[10px] font-bold text-[#2A9D8F] uppercase tracking-wider"
              >
                Remise -{Number(order.discount_total).toFixed(2)} CHF
              </div>
            )}
          </div>
        </div>

        {(order.applied_promotions || []).length > 0 && (
          <div
            data-testid={`order-promos-${order.order_number}`}
            className="text-[11px] bg-[#2A9D8F]/10 border-l-2 border-[#2A9D8F] px-2 py-1 rounded mb-2"
          >
            <span className="font-bold uppercase tracking-wider text-[10px] mr-1">Promo :</span>
            {(order.applied_promotions || []).join(" · ")}
          </div>
        )}

        <div className="text-sm">
          <div className="font-bold">{order.first_name} {order.last_name}</div>
          <div className="text-neutral-500 text-xs flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
            {order.phone && (<span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{order.phone}</span>)}
            {order.email && (<span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{order.email}</span>)}
          </div>
          {order.order_type === "delivery" && (
            <div className="text-neutral-600 text-xs mt-1.5 inline-flex items-start gap-1">
              <MapPin className="w-3.5 h-3.5 mt-0.5" /> {order.address} · {order.postal_code} {order.city}
            </div>
          )}
        </div>

        <div className="border-t border-dashed border-neutral-200 pt-3">
          <ul className="text-sm space-y-1.5">
            {order.items.map((it, idx) => (
              <li key={idx} className="">
                <div className="flex justify-between">
                  <span><span className="font-bold mr-1">{it.quantity}×</span> {it.name}</span>
                  <span className="text-neutral-500">{fmtCHF(it.line_total != null ? it.line_total : (it.price + (it.addons_total || 0)) * it.quantity)}</span>
                </div>
                {(it.selected_addons || []).length > 0 && (
                  <ul className="ml-5 text-[11px] text-neutral-500 mt-0.5">
                    {it.selected_addons.map((a, i) => (
                      <li key={i}>+ {a.name}{Number(a.price) > 0 ? ` (${Number(a.price).toFixed(2)} CHF)` : ""}</li>
                    ))}
                  </ul>
                )}
                {it.comment && (
                  <div
                    data-testid={`item-comment-${order.order_number}-${idx}`}
                    className="ml-5 mt-1 text-[11px] font-medium bg-[#E9C46A]/30 border-l-2 border-[#E9C46A] px-2 py-1 rounded text-neutral-800"
                  >
                    <span className="font-bold uppercase tracking-wider text-[9px] mr-1">Note :</span>
                    {it.comment}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {order.note && (
            <div className="mt-2 text-xs bg-[#E9C46A]/20 border-l-2 border-[#E9C46A] px-2 py-1.5 rounded">
              <span className="font-bold uppercase tracking-wider text-[10px]">Note globale :</span> {order.note}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-5 py-3 border-t border-neutral-100 bg-neutral-50 flex flex-wrap gap-2">
        {nextStatus && order.status !== "annule" && order.status !== "termine" && (
          <Button
            onClick={() => onSetStatus(order, nextStatus)}
            data-testid={`order-${order.order_number}-advance`}
            className="bg-[#0A0A0A] hover:bg-[#222] text-white rounded-md h-9 text-xs font-bold uppercase tracking-wider"
          >
            <Check className="w-3.5 h-3.5 mr-1" />
            {isPending ? "Valider" : `→ ${STATUS_LABELS[nextStatus]}`}
          </Button>
        )}
        {isPending && (
          <Button
            onClick={() => onSetStatus(order, "annule")}
            variant="outline"
            data-testid={`order-${order.order_number}-reject`}
            className="border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white rounded-md h-9 text-xs font-bold uppercase tracking-wider"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Refuser
          </Button>
        )}
        <Button
          onClick={() => onReprint && onReprint(order)}
          variant="outline"
          data-testid={`order-${order.order_number}-reprint`}
          className="border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white rounded-md h-9 text-xs font-bold uppercase tracking-wider ml-auto"
          title="Renvoyer le ticket à l'imprimante Star (CloudPRNT)"
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> Réimprimer
        </Button>
        <Button
          onClick={() => onPrint(order)}
          variant="outline"
          data-testid={`order-${order.order_number}-print`}
          className="border-neutral-300 hover:bg-neutral-100 rounded-md h-9 text-xs font-bold uppercase tracking-wider"
          title="Aperçu HTML (navigateur)"
        >
          <Printer className="w-3.5 h-3.5 mr-1" /> Aperçu
        </Button>
        {(order.status === "termine" || order.status === "annule") && onDelete && (
          <Button
            onClick={() => onDelete(order)}
            variant="outline"
            data-testid={`order-${order.order_number}-delete`}
            className="border-red-300 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600 rounded-md h-9 text-xs font-bold uppercase tracking-wider"
            title="Supprimer définitivement de l'affichage"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
          </Button>
        )}
      </div>
    </div>
  );
};

// ---------- PRODUCTS PANEL ----------
const emptyProduct = { name: "", description: "", price: 0, category: "", image_url: "", available: true, addon_groups: [] };

const ProductsPanel = ({ products, reload, settings, onSettingsSaved }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copySource, setCopySource] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [copying, setCopying] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const openCreate = () => { setEditing(null); setForm(emptyProduct); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProduct, ...p, addon_groups: p.addon_groups || [] }); setOpen(true); };
  const openCopy = (p) => { setCopySource(p); setCopyTargets([]); };

  const doCopy = async () => {
    if (!copySource || copyTargets.length === 0) {
      toast.error("Sélectionnez au moins un produit cible");
      return;
    }
    setCopying(true);
    try {
      await api.post(`/admin/products/${copySource.id}/copy-addons`, { target_product_ids: copyTargets });
      toast.success(`Addons copiés vers ${copyTargets.length} produit(s)`);
      setCopySource(null);
      setCopyTargets([]);
      await reload();
    } catch (e) {
      toast.error("Erreur lors de la copie");
    } finally {
      setCopying(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.category || form.price <= 0) {
      toast.error("Nom, catégorie et prix sont requis");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) };
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit ajouté");
      }
      setOpen(false);
      await reload();
    } catch (e) {
      toast.error("Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produit supprimé");
      await reload();
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    products.forEach((p) => {
      g[p.category] = g[p.category] || [];
      g[p.category].push(p);
    });
    return g;
  }, [products]);

  // Order categories according to settings.category_order, fallback to natural order
  const orderedCategories = useMemo(() => {
    const allCats = Object.keys(grouped);
    const saved = Array.isArray(settings?.category_order) ? settings.category_order : [];
    const known = saved.filter((c) => allCats.includes(c));
    const extras = allCats.filter((c) => !known.includes(c));
    return [...known, ...extras];
  }, [grouped, settings]);

  const moveCategory = async (cat, dir) => {
    const list = [...orderedCategories];
    const idx = list.indexOf(cat);
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target], list[idx]];
    setSavingOrder(true);
    try {
      await api.put("/admin/settings", { category_order: list });
      toast.success("Ordre des catégories mis à jour");
      if (onSettingsSaved) await onSettingsSaved();
    } catch (e) {
      toast.error("Erreur lors de la sauvegarde de l'ordre");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-2xl font-black tracking-tight">Produits</h2>
          <p className="text-sm text-neutral-500">{products.length} plats au menu</p>
        </div>
        <Button onClick={openCreate} data-testid="add-product-btn" className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-10 px-3 sm:px-5 font-bold text-xs sm:text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter un plat
        </Button>
      </div>

      {orderedCategories.map((cat, idx) => {
        const list = grouped[cat] || [];
        return (
        <div key={cat} className="mb-8" data-testid={`category-section-${cat}`}>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">{cat}</h3>
            <span className="text-[10px] text-neutral-400">({list.length})</span>
            <div className="ml-2 flex items-center gap-1">
              <button
                data-testid={`cat-up-${cat}`}
                disabled={idx === 0 || savingOrder}
                onClick={() => moveCategory(cat, -1)}
                className="w-6 h-6 rounded border border-neutral-300 flex items-center justify-center text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Monter"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`cat-down-${cat}`}
                disabled={idx === orderedCategories.length - 1 || savingOrder}
                onClick={() => moveCategory(cat, +1)}
                className="w-6 h-6 rounded border border-neutral-300 flex items-center justify-center text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Descendre"
              >
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((p) => (
              <div key={p.id} data-testid={`product-row-${p.id}`} className="bg-white border border-neutral-200 rounded-lg p-3 flex gap-3">
                <div className="w-20 h-20 rounded-md bg-neutral-100 overflow-hidden flex-shrink-0">
                  {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2">
                    <div className="font-bold truncate">{p.name}</div>
                    <div className="font-black whitespace-nowrap">{Number(p.price).toFixed(2)} CHF</div>
                  </div>
                  <p className="text-xs text-neutral-500 line-clamp-2 mt-1">{p.description}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {!p.available && <span className="text-[10px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded">Indispo</span>}
                    {(p.addon_groups?.length > 0) && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E9C46A]/30 text-neutral-800 px-1.5 py-0.5 rounded" data-testid={`product-${p.id}-addons-count`}>
                        {p.addon_groups.reduce((acc, g) => acc + (g.options?.length || 0), 0)} addons
                      </span>
                    )}
                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="text-xs font-bold text-neutral-700 hover:text-[#D90429] inline-flex items-center gap-1">
                      <Pencil className="w-3 h-3" /> Modifier
                    </button>
                    {(p.addon_groups?.length > 0) && (
                      <button onClick={() => openCopy(p)} data-testid={`copy-addons-${p.id}`} className="text-xs font-bold text-neutral-700 hover:text-[#2A9D8F] inline-flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Copier addons
                      </button>
                    )}
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="text-xs font-bold text-[#D90429] hover:underline inline-flex items-center gap-1 ml-auto">
                      <Trash2 className="w-3 h-3" /> Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        );
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier le plat" : "Nouveau plat"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Nom</Label>
              <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Description</Label>
              <Textarea data-testid="product-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Prix (CHF)</Label>
                <Input data-testid="product-price-input" type="number" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Catégorie</Label>
                <Input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Soupes Phở" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">URL Image</Label>
              <Input data-testid="product-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="product-available-switch" checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} />
              <Label className="text-sm">Disponible à la commande</Label>
            </div>

            {/* ADDONS EDITOR */}
            <AddonGroupsEditor
              groups={form.addon_groups || []}
              onChange={(groups) => setForm({ ...form, addon_groups: groups })}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="save-product-btn" className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* COPY ADDONS DIALOG */}
      <Dialog open={!!copySource} onOpenChange={(v) => { if (!v) { setCopySource(null); setCopyTargets([]); } }}>
        <DialogContent data-testid="copy-addons-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-lg w-[calc(100vw-1rem)] sm:w-full max-h-[calc(100dvh-1rem)] sm:max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Copier les addons de &quot;{copySource?.name}&quot;</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            Sélectionnez les produits qui recevront les <strong>{copySource?.addon_groups?.length || 0}</strong> groupe(s) d&apos;addons de ce plat.
            Les addons existants des produits cibles seront <strong className="text-[#D90429]">remplacés</strong>.
          </p>
          <div className="space-y-1 max-h-80 overflow-y-auto border border-neutral-200 rounded-md p-2">
            {products.filter((p) => p.id !== copySource?.id).map((p) => {
              const checked = copyTargets.includes(p.id);
              return (
                <label key={p.id} className="flex items-center gap-3 px-2 py-2 hover:bg-neutral-50 rounded cursor-pointer" data-testid={`copy-target-${p.id}`}>
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(v) => {
                      setCopyTargets((prev) => v ? [...prev, p.id] : prev.filter((id) => id !== p.id));
                    }}
                  />
                  <span className="flex-1 text-sm">
                    <span className="font-bold">{p.name}</span>
                    <span className="text-neutral-400 text-xs ml-2">{p.category}</span>
                  </span>
                  {(p.addon_groups?.length > 0) && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#E9C46A]/30 text-neutral-800 px-1.5 py-0.5 rounded">
                      a déjà {p.addon_groups.length} grp.
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-xs text-neutral-500">
            <button
              type="button"
              onClick={() => setCopyTargets(products.filter((p) => p.id !== copySource?.id).map((p) => p.id))}
              data-testid="copy-select-all"
              className="font-bold hover:text-[#0A0A0A] underline"
            >
              Tout sélectionner
            </button>
            <span>{copyTargets.length} produit(s) sélectionné(s)</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCopySource(null); setCopyTargets([]); }} className="rounded-md">Annuler</Button>
            <Button onClick={doCopy} disabled={copying || copyTargets.length === 0} data-testid="confirm-copy-addons-btn" className="bg-[#2A9D8F] hover:bg-[#258377] text-white rounded-md">
              {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Copy className="w-4 h-4 mr-1.5" />Copier vers {copyTargets.length}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- ADDON GROUPS EDITOR ----------
const newOption = () => ({ id: `opt_${Math.random().toString(36).slice(2, 9)}`, name: "", price: 0 });
const newGroup = () => ({
  id: `grp_${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  required: false,
  multi_select: true,
  min_select: 0,
  max_select: 0,
  options: [newOption()],
});

const AddonGroupsEditor = ({ groups, onChange }) => {
  const update = (idx, patch) => {
    const next = groups.map((g, i) => (i === idx ? { ...g, ...patch } : g));
    onChange(next);
  };
  const remove = (idx) => onChange(groups.filter((_, i) => i !== idx));
  const add = () => onChange([...(groups || []), newGroup()]);

  const updateOption = (gIdx, oIdx, patch) => {
    const opts = groups[gIdx].options.map((o, i) => (i === oIdx ? { ...o, ...patch } : o));
    update(gIdx, { options: opts });
  };
  const addOption = (gIdx) => update(gIdx, { options: [...(groups[gIdx].options || []), newOption()] });
  const removeOption = (gIdx, oIdx) =>
    update(gIdx, { options: groups[gIdx].options.filter((_, i) => i !== oIdx) });

  return (
    <div className="border border-neutral-200 rounded-md p-4 bg-neutral-50/60" data-testid="addon-groups-editor">
      <div className="flex items-center justify-between mb-3">
        <div>
          <Label className="text-xs uppercase tracking-wider font-bold">Groupes d&apos;addons / suppléments</Label>
          <p className="text-[11px] text-neutral-500">Ex: « Suppléments » avec « +Œuf 2 CHF », « +Bœuf 4 CHF ».</p>
        </div>
        <Button type="button" onClick={add} data-testid="add-addon-group-btn"
          className="bg-[#2A9D8F] hover:bg-[#258377] text-white rounded-md h-8 px-3 text-xs font-bold">
          <Plus className="w-3.5 h-3.5 mr-1" /> Groupe
        </Button>
      </div>

      {(!groups || groups.length === 0) && (
        <p className="text-xs text-neutral-500 italic">Aucun groupe d&apos;addons. Cliquez sur « Groupe » pour en ajouter un.</p>
      )}

      <div className="space-y-3">
        {groups.map((g, gIdx) => (
          <div key={g.id || gIdx} className="bg-white border border-neutral-200 rounded-md p-3" data-testid={`addon-group-${gIdx}`}>
            <div className="flex items-start gap-2 mb-2">
              <Input
                value={g.name}
                onChange={(e) => update(gIdx, { name: e.target.value })}
                placeholder="Nom du groupe (ex: Suppléments)"
                data-testid={`addon-group-${gIdx}-name`}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => remove(gIdx)}
                data-testid={`remove-addon-group-${gIdx}`}
                className="text-[#D90429] hover:bg-red-50 p-2 rounded-md"
                title="Supprimer le groupe"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 text-xs">
              <label className="flex items-center gap-1.5">
                <Checkbox
                  checked={!!g.required}
                  onCheckedChange={(v) => update(gIdx, { required: !!v })}
                  data-testid={`addon-group-${gIdx}-required`}
                />
                Obligatoire
              </label>
              <label className="flex items-center gap-1.5">
                <Checkbox
                  checked={!!g.multi_select}
                  onCheckedChange={(v) => update(gIdx, { multi_select: !!v })}
                  data-testid={`addon-group-${gIdx}-multi`}
                />
                Choix multiple
              </label>
              <div className="flex items-center gap-1.5">
                <span>Min</span>
                <Input
                  type="number" min="0"
                  value={g.min_select || 0}
                  onChange={(e) => update(gIdx, { min_select: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span>Max</span>
                <Input
                  type="number" min="0"
                  value={g.max_select || 0}
                  onChange={(e) => update(gIdx, { max_select: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              {(g.options || []).map((o, oIdx) => (
                <div key={o.id || oIdx} className="flex items-center gap-2" data-testid={`addon-option-${gIdx}-${oIdx}`}>
                  <Input
                    value={o.name}
                    onChange={(e) => updateOption(gIdx, oIdx, { name: e.target.value })}
                    placeholder="Nom (ex: +Œuf)"
                    className="flex-1 h-9"
                    data-testid={`addon-option-${gIdx}-${oIdx}-name`}
                  />
                  <Input
                    type="number" step="0.5"
                    value={o.price}
                    onChange={(e) => updateOption(gIdx, oIdx, { price: Number(e.target.value) })}
                    placeholder="CHF"
                    className="w-24 h-9"
                    data-testid={`addon-option-${gIdx}-${oIdx}-price`}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(gIdx, oIdx)}
                    data-testid={`remove-addon-option-${gIdx}-${oIdx}`}
                    className="text-neutral-500 hover:text-[#D90429] p-1.5 rounded-md"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                onClick={() => addOption(gIdx)}
                variant="outline"
                data-testid={`add-addon-option-${gIdx}`}
                className="h-8 rounded-md border-dashed border-neutral-300 text-xs"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Option
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- HOURS PANEL ----------
const DAYS = [
  { idx: "1", label: "Lundi" },
  { idx: "2", label: "Mardi" },
  { idx: "3", label: "Mercredi" },
  { idx: "4", label: "Jeudi" },
  { idx: "5", label: "Vendredi" },
  { idx: "6", label: "Samedi" },
  { idx: "0", label: "Dimanche" },
];
// ============== PROMOTIONS PANEL ==============

const PROMO_TYPE_LABELS = {
  global_percent: "% sur toute la commande",
  product_percent: "% sur produits sélectionnés",
  product_fixed_price: "Nouveau prix fixe sur produits",
  product_amount_off: "Réduction CHF par unité sur produits",
  bogo: "1 acheté = 1 offert (sur produits)",
};

const emptyPromo = {
  name: "",
  type: "global_percent",
  value: 10,
  product_ids: [],
  active: true,
  starts_at: "",
  ends_at: "",
  code: "",
  min_subtotal: 0,
};

const PromotionsPanel = ({ products }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPromo);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promotions");
      setList(data || []);
    } catch (e) {
      toast.error("Erreur chargement promotions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyPromo); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...emptyPromo,
      ...p,
      product_ids: p.product_ids || [],
      starts_at: p.starts_at || "",
      ends_at: p.ends_at || "",
      code: p.code || "",
      min_subtotal: p.min_subtotal || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    const needsProducts = ["product_percent", "product_fixed_price", "product_amount_off", "bogo"].includes(form.type);
    if (needsProducts && form.product_ids.length === 0) {
      toast.error("Sélectionnez au moins un produit pour ce type de promotion");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        min_subtotal: Number(form.min_subtotal) || 0,
        code: (form.code || "").trim().toUpperCase(),
      };
      if (editing) {
        await api.put(`/admin/promotions/${editing.id}`, payload);
        toast.success("Promotion mise à jour");
      } else {
        await api.post("/admin/promotions", payload);
        toast.success("Promotion créée");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/promotions/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Promotion désactivée" : "Promotion activée");
      await load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la promotion "${p.name}" ?`)) return;
    try {
      await api.delete(`/admin/promotions/${p.id}`);
      toast.success("Promotion supprimée");
      await load();
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const toggleProduct = (pid) => {
    setForm((f) => {
      const has = f.product_ids.includes(pid);
      return { ...f, product_ids: has ? f.product_ids.filter((x) => x !== pid) : [...f.product_ids, pid] };
    });
  };

  const needsProducts = ["product_percent", "product_fixed_price", "product_amount_off", "bogo"].includes(form.type);
  const isPercent = form.type === "global_percent" || form.type === "product_percent";
  const isFixedPrice = form.type === "product_fixed_price";
  const isAmountOff = form.type === "product_amount_off";
  const isBogo = form.type === "bogo";

  return (
    <div data-testid="promotions-panel">
      <div className="flex flex-wrap items-center justify-between mb-5 gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-black tracking-tight">Promotions</h2>
          <p className="text-sm text-neutral-500">{list.length} promotion(s) — appliquées automatiquement ou via code</p>
        </div>
        <Button onClick={openCreate} data-testid="add-promo-btn" className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-10 px-3 sm:px-5 font-bold text-xs sm:text-sm">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle promotion
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-neutral-300 rounded-lg">
          <Percent className="w-10 h-10 mx-auto text-neutral-300 mb-3" />
          <p className="text-sm text-neutral-500">Aucune promotion pour l&apos;instant.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((p) => (
            <div key={p.id} data-testid={`promo-row-${p.id}`} className="bg-white border border-neutral-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black truncate">{p.name}</span>
                  {p.code && (
                    <span className="text-[10px] font-black uppercase tracking-wider bg-[#0A0A0A] text-white px-2 py-0.5 rounded" data-testid={`promo-code-${p.id}`}>
                      Code : {p.code}
                    </span>
                  )}
                  {p.active ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#2A9D8F]/20 text-[#0F766E] px-1.5 py-0.5 rounded">Active</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded">Désactivée</span>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  {PROMO_TYPE_LABELS[p.type] || p.type}
                  {" · "}
                  {p.type === "product_fixed_price" ? `Nouveau prix : ${Number(p.value).toFixed(2)} CHF` :
                   p.type === "product_amount_off" ? `-${Number(p.value).toFixed(2)} CHF / unité` :
                   p.type === "bogo" ? "1 = 1 offert" :
                   `-${Number(p.value)}%`}
                  {(p.product_ids?.length > 0) && ` · ${p.product_ids.length} produit(s)`}
                  {Number(p.min_subtotal) > 0 && ` · min ${Number(p.min_subtotal).toFixed(2)} CHF`}
                  {p.starts_at && ` · début ${p.starts_at}`}
                  {p.ends_at && ` · fin ${p.ends_at}`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`promo-active-${p.id}`} />
                <button onClick={() => openEdit(p)} data-testid={`edit-promo-${p.id}`} className="text-xs font-bold text-neutral-700 hover:text-[#D90429] inline-flex items-center gap-1 px-2 py-1">
                  <Pencil className="w-3.5 h-3.5" /> Modifier
                </button>
                <button onClick={() => remove(p)} data-testid={`delete-promo-${p.id}`} className="text-xs font-bold text-[#D90429] hover:underline inline-flex items-center gap-1 px-2 py-1">
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="promo-dialog" onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier la promotion" : "Nouvelle promotion"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Nom interne</Label>
              <Input data-testid="promo-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : -10% printemps" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Type</Label>
              <select
                data-testid="promo-type-select"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value, product_ids: [] })}
                className="w-full h-10 border border-neutral-300 rounded-md px-3 text-sm bg-white"
              >
                {Object.entries(PROMO_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {!isBogo && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">
                  {isPercent ? "Pourcentage (%)" : isFixedPrice ? "Nouveau prix (CHF)" : isAmountOff ? "Réduction (CHF / unité)" : "Valeur"}
                </Label>
                <Input
                  data-testid="promo-value-input"
                  type="number"
                  step={isFixedPrice || isAmountOff ? "0.50" : "1"}
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Code promo (optionnel)</Label>
                <Input
                  data-testid="promo-code-input"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                  placeholder="Ex : BIENVENUE10"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Si vide → promotion appliquée automatiquement. Si rempli → uniquement avec le code.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Min. panier (CHF)</Label>
                <Input
                  data-testid="promo-min-input"
                  type="number"
                  step="0.50"
                  value={form.min_subtotal}
                  onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Début (optionnel)</Label>
                <Input data-testid="promo-start-input" type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Fin (optionnel)</Label>
                <Input data-testid="promo-end-input" type="date" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active-switch" />
              <Label>Active</Label>
            </div>

            {needsProducts && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold mb-2 block">Produits concernés ({form.product_ids.length} sélectionné{form.product_ids.length > 1 ? "s" : ""})</Label>
                <div className="border border-neutral-200 rounded-md max-h-64 overflow-y-auto">
                  {products.map((p) => (
                    <label
                      key={p.id}
                      data-testid={`promo-product-${p.id}`}
                      className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 cursor-pointer"
                    >
                      <Checkbox checked={form.product_ids.includes(p.id)} onCheckedChange={() => toggleProduct(p.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{p.name}</div>
                        <div className="text-xs text-neutral-500">{p.category} · {Number(p.price).toFixed(2)} CHF</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-promo-btn" onClick={save} disabled={saving} className="bg-[#D90429] hover:bg-[#BF0424] text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};



const HoursPanel = ({ settings, onSaved }) => {
  const [hours, setHours] = useState(() => {
    const base = { "0": [], "1": [], "2": [], "3": [], "4": [], "5": [], "6": [] };
    const incoming = settings?.opening_hours || {};
    Object.keys(base).forEach((k) => {
      base[k] = Array.isArray(incoming[k]) ? incoming[k] : [];
    });
    return base;
  });
  const [closedDates, setClosedDates] = useState(() =>
    Array.isArray(settings?.closed_dates) ? [...settings.closed_dates].sort() : []
  );
  const [newClosedDate, setNewClosedDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingClosed, setSavingClosed] = useState(false);

  const addWindow = (day) => {
    setHours((h) => ({ ...h, [day]: [...(h[day] || []), { open: "11:30", close: "14:00" }] }));
  };
  const removeWindow = (day, idx) => {
    setHours((h) => ({ ...h, [day]: h[day].filter((_, i) => i !== idx) }));
  };
  const updateWindow = (day, idx, patch) => {
    setHours((h) => ({
      ...h,
      [day]: h[day].map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    }));
  };
  const toggleClosed = (day, closed) => {
    // closed=true → empty array (Fermé toute la journée)
    // closed=false → restore a default window so the editor is usable
    setHours((h) => ({
      ...h,
      [day]: closed ? [] : [{ open: "11:30", close: "14:00" }],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { opening_hours: hours });
      toast.success("Horaires enregistrés");
      onSaved();
    } catch (e) {
      toast.error("Erreur enregistrement horaires");
    } finally {
      setSaving(false);
    }
  };

  const todayIso = () => new Date().toISOString().slice(0, 10);

  const persistClosedDates = async (list) => {
    setSavingClosed(true);
    try {
      const res = await api.put("/admin/settings", { closed_dates: list });
      const cleaned = Array.isArray(res.data?.closed_dates) ? [...res.data.closed_dates].sort() : list;
      setClosedDates(cleaned);
      onSaved();
    } catch (e) {
      toast.error("Erreur enregistrement fermetures");
    } finally {
      setSavingClosed(false);
    }
  };

  const addClosedDate = async () => {
    const val = (newClosedDate || "").trim();
    if (!val) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      toast.error("Format de date invalide");
      return;
    }
    if (closedDates.includes(val)) {
      toast.error("Date déjà ajoutée");
      return;
    }
    await persistClosedDates([...closedDates, val].sort());
    setNewClosedDate("");
    toast.success("Fermeture enregistrée");
  };

  const addClosedRange = async (start, end) => {
    if (!start || !end) {
      toast.error("Sélectionnez une date de début et de fin");
      return;
    }
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s) || isNaN(e) || s > e) {
      toast.error("Plage invalide");
      return;
    }
    const days = [];
    const cur = new Date(s);
    while (cur <= e) {
      days.push(cur.toISOString().slice(0, 10));
      cur.setDate(cur.getDate() + 1);
    }
    const merged = Array.from(new Set([...closedDates, ...days])).sort();
    await persistClosedDates(merged);
    toast.success(`${days.length} jour(s) enregistré(s)`);
  };

  const removeClosedDate = async (d) => {
    await persistClosedDates(closedDates.filter((x) => x !== d));
  };

  const formatClosedDate = (iso) => {
    try {
      const [y, m, dd] = iso.split("-").map(Number);
      const dt = new Date(y, m - 1, dd);
      return dt.toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    } catch {
      return iso;
    }
  };

  const upcomingClosed = closedDates.filter((d) => d >= todayIso());
  const pastClosed = closedDates.filter((d) => d < todayIso());

  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-black tracking-tight mb-1">Horaires d&apos;ouverture</h2>
      <p className="text-sm text-neutral-500 mb-6">Ajoutez plusieurs créneaux par jour (ex: midi 11:30–14:00, soir 18:00–22:00).</p>

      <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-3">
        {DAYS.map((d) => {
          const dayWindows = hours[d.idx] || [];
          const isClosed = dayWindows.length === 0;
          return (
          <div key={d.idx} className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-3 items-start py-2 border-b last:border-b-0 border-neutral-100" data-testid={`hours-day-${d.idx}`}>
            <div className="pt-2">
              <div className="font-bold text-sm">{d.label}</div>
              <label className="mt-1.5 flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-neutral-600 cursor-pointer">
                <Switch
                  checked={isClosed}
                  onCheckedChange={(v) => toggleClosed(d.idx, v)}
                  data-testid={`day-closed-toggle-${d.idx}`}
                />
                <span className={isClosed ? "text-[#D90429]" : "text-neutral-500"}>Fermé</span>
              </label>
            </div>
            <div className="space-y-2">
              {isClosed ? (
                <div
                  data-testid={`day-closed-label-${d.idx}`}
                  className="text-xs font-bold text-[#D90429] bg-[#D90429]/5 border border-dashed border-[#D90429]/40 rounded-md px-3 py-2 inline-block"
                >
                  Fermé toute la journée
                </div>
              ) : (
                <>
                  {dayWindows.map((w, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`hours-window-${d.idx}-${i}`}>
                      <Input
                        type="time"
                        value={w.open}
                        onChange={(e) => updateWindow(d.idx, i, { open: e.target.value })}
                        className="h-9 w-32"
                        data-testid={`hours-open-${d.idx}-${i}`}
                      />
                      <span className="text-neutral-400">→</span>
                      <Input
                        type="time"
                        value={w.close}
                        onChange={(e) => updateWindow(d.idx, i, { close: e.target.value })}
                        className="h-9 w-32"
                        data-testid={`hours-close-${d.idx}-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeWindow(d.idx, i)}
                        data-testid={`remove-hours-${d.idx}-${i}`}
                        className="text-neutral-500 hover:text-[#D90429] p-1.5 rounded-md"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    onClick={() => addWindow(d.idx)}
                    variant="outline"
                    data-testid={`add-hours-${d.idx}`}
                    className="h-8 rounded-md border-dashed text-xs"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Créneau
                  </Button>
                </>
              )}
            </div>
          </div>
          );
        })}
        <div className="flex justify-end pt-3">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-hours-btn"
            className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 px-6 font-bold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer les horaires"}
          </Button>
        </div>
      </div>

      {/* Exceptional closures (vacations & holidays) */}
      <div className="mt-8" data-testid="closed-dates-section">
        <h3 className="text-xl font-black tracking-tight mb-1">Fermetures exceptionnelles</h3>
        <p className="text-sm text-neutral-500 mb-4">
          Ajoutez des jours de fermeture ponctuels : vacances, jours fériés, événements privés. Les clients
          ne pourront ni commander ni programmer une commande pour ces dates.
        </p>

        <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-5">
          {/* Add single date */}
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Ajouter une date</Label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={newClosedDate}
                onChange={(e) => setNewClosedDate(e.target.value)}
                min={todayIso()}
                data-testid="closed-date-input"
                className="h-10 w-48"
              />
              <Button
                onClick={addClosedDate}
                disabled={savingClosed || !newClosedDate}
                data-testid="add-closed-date-btn"
                className="bg-[#0A0A0A] hover:bg-neutral-800 text-white rounded-md h-10 px-4 font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
          </div>

          {/* Add range */}
          <div className="pt-4 border-t border-neutral-100">
            <Label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Ajouter une plage (vacances)</Label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={rangeStart}
                onChange={(e) => setRangeStart(e.target.value)}
                min={todayIso()}
                data-testid="closed-range-start"
                className="h-10 w-48"
              />
              <span className="text-neutral-500 text-sm">→</span>
              <Input
                type="date"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value)}
                min={rangeStart || todayIso()}
                data-testid="closed-range-end"
                className="h-10 w-48"
              />
              <Button
                onClick={async () => {
                  await addClosedRange(rangeStart, rangeEnd);
                  setRangeStart("");
                  setRangeEnd("");
                }}
                disabled={savingClosed || !rangeStart || !rangeEnd}
                data-testid="add-closed-range-btn"
                className="bg-[#0A0A0A] hover:bg-neutral-800 text-white rounded-md h-10 px-4 font-bold"
              >
                <Plus className="w-4 h-4 mr-1" /> Ajouter la plage
              </Button>
            </div>
          </div>

          {/* List */}
          <div className="pt-4 border-t border-neutral-100">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
                Dates fermées à venir ({upcomingClosed.length})
              </Label>
            </div>
            {upcomingClosed.length === 0 ? (
              <p className="text-xs text-neutral-500 italic" data-testid="closed-dates-empty">
                Aucune fermeture programmée.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="closed-dates-list">
                {upcomingClosed.map((d) => (
                  <div
                    key={d}
                    data-testid={`closed-date-item-${d}`}
                    className="inline-flex items-center gap-2 bg-[#E9C46A]/20 border border-[#E9C46A] text-neutral-900 rounded-md px-3 py-1.5 text-xs font-bold"
                  >
                    <span className="capitalize">{formatClosedDate(d)}</span>
                    <button
                      type="button"
                      onClick={() => removeClosedDate(d)}
                      className="text-neutral-600 hover:text-[#D90429]"
                      data-testid={`remove-closed-date-${d}`}
                      title="Retirer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {pastClosed.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-neutral-500 cursor-pointer hover:text-neutral-800">
                  Historique ({pastClosed.length})
                </summary>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {pastClosed.slice(-30).map((d) => (
                    <span key={d} className="text-[11px] text-neutral-400 line-through">
                      {d}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- SETTINGS PANEL ----------
const SettingsPanel = ({ settings, onSaved }) => {
  const [form, setForm] = useState({
    accepting_orders: !!settings.accepting_orders,
    prep_time_minutes: settings.prep_time_minutes ?? 15,
    base_delivery_time: settings.base_delivery_time ?? 20,
    printer_enabled: !!settings.printer_enabled,
    printer_ip: settings.printer_ip ?? "",
    printer_auto_cut: settings.printer_auto_cut !== false,
  });
  const [postalCodes, setPostalCodes] = useState(
    Array.isArray(settings.postal_codes) ? settings.postal_codes.map((p) => ({ ...p })) : []
  );
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Marketing opt-in subscribers
  const [marketingSubs, setMarketingSubs] = useState([]);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingExpanded, setMarketingExpanded] = useState(false);

  const loadMarketing = useCallback(async () => {
    setMarketingLoading(true);
    try {
      const { data } = await api.get("/admin/marketing/emails");
      setMarketingSubs(Array.isArray(data?.subscribers) ? data.subscribers : []);
    } catch (e) {
      toast.error("Erreur chargement emails marketing");
    } finally {
      setMarketingLoading(false);
    }
  }, []);

  useEffect(() => { loadMarketing(); }, [loadMarketing]);

  const copyAllEmails = async () => {
    const emails = marketingSubs.map((s) => s.email).filter(Boolean).join(", ");
    if (!emails) {
      toast.error("Aucun email à copier");
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`${marketingSubs.length} email(s) copié(s)`);
    } catch (e) {
      toast.error("Échec de la copie");
    }
  };

  const downloadCsv = () => {
    if (marketingSubs.length === 0) {
      toast.error("Aucun email à exporter");
      return;
    }
    const header = "Email,Prénom,Nom,Date opt-in,Commandes,Dernière commande\n";
    const rows = marketingSubs.map((s) => {
      const fields = [
        s.email || "",
        s.first_name || "",
        s.last_name || "",
        s.marketing_opt_in_at || "",
        s.total_orders ?? "",
        s.last_order_at || "",
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`);
      return fields.join(",");
    });
    const csv = header + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quang-marketing-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const addPostal = () =>
    setPostalCodes((arr) => [...arr, { code: "", min_order: 20, delivery_fee: 5 }]);
  const removePostal = (idx) => setPostalCodes((arr) => arr.filter((_, i) => i !== idx));
  const updatePostal = (idx, patch) =>
    setPostalCodes((arr) => arr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const testPrint = async () => {
    if (!form.printer_ip) {
      toast.error("Renseignez d'abord l'IP de l'imprimante");
      return;
    }
    setTesting(true);
    const res = await sendToStarPrinter({ ip: form.printer_ip, xml: buildTestXml() });
    if (res.ok) toast.success("Test envoyé à l'imprimante");
    else toast.error("Échec: " + res.message);
    setTesting(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = postalCodes
        .map((p) => ({
          code: String(p.code || "").trim(),
          min_order: Number(p.min_order) || 0,
          delivery_fee: Number(p.delivery_fee) || 0,
        }))
        .filter((p) => p.code);

      const payload = {
        accepting_orders: !!form.accepting_orders,
        prep_time_minutes: Number(form.prep_time_minutes),
        base_delivery_time: Number(form.base_delivery_time),
        postal_codes: cleaned,
        printer_enabled: !!form.printer_enabled,
        printer_ip: String(form.printer_ip || "").trim(),
        printer_auto_cut: !!form.printer_auto_cut,
      };
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      onSaved();
    } catch (e) {
      toast.error("Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <h2 className="text-2xl font-black tracking-tight mb-1">Paramètres</h2>
      <p className="text-sm text-neutral-500 mb-6">Codes postaux livrables, minimums et disponibilité</p>

      <div className="bg-white border border-neutral-200 rounded-lg p-6 space-y-6">
        <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-md">
          <div>
            <div className="font-bold">Accepter les commandes</div>
            <p className="text-xs text-neutral-500">Désactivez pour fermer temporairement la prise de commande.</p>
          </div>
          <Switch
            data-testid="accepting-orders-switch"
            checked={!!form.accepting_orders}
            onCheckedChange={(v) => setForm({ ...form, accepting_orders: v })}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Préparation (min)</Label>
            <Input
              data-testid="prep-time-input"
              type="number"
              value={form.prep_time_minutes}
              onChange={(e) => setForm({ ...form, prep_time_minutes: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold">Temps livraison base (min)</Label>
            <Input
              data-testid="base-delivery-time-input"
              type="number"
              value={form.base_delivery_time}
              onChange={(e) => setForm({ ...form, base_delivery_time: e.target.value })}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Codes postaux livrables</Label>
              <p className="text-xs text-neutral-500">Seuls les codes listés ici sont livrables. Définissez minimum & frais par code.</p>
            </div>
            <Button
              type="button"
              onClick={addPostal}
              data-testid="add-postal-code-btn"
              className="bg-[#2A9D8F] hover:bg-[#258377] text-white rounded-md h-9 px-3 text-xs font-bold"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Code postal
            </Button>
          </div>

          {postalCodes.length === 0 && (
            <p className="text-xs text-neutral-500 italic border border-dashed border-neutral-300 rounded-md p-4 text-center">
              Aucun code postal configuré — aucune livraison ne sera acceptée.
            </p>
          )}

          <div className="space-y-2">
            {postalCodes.map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-[120px_1fr_1fr_40px] gap-2 items-center bg-neutral-50 border border-neutral-200 rounded-md p-2" data-testid={`postal-row-${idx}`}>
                <Input
                  value={p.code}
                  onChange={(e) => updatePostal(idx, { code: e.target.value })}
                  placeholder="1018"
                  className="h-9"
                  data-testid={`postal-code-${idx}`}
                />
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-neutral-500 whitespace-nowrap">Min CHF</span>
                  <Input
                    type="number" step="0.5"
                    value={p.min_order}
                    onChange={(e) => updatePostal(idx, { min_order: e.target.value })}
                    className="h-9"
                    data-testid={`postal-min-${idx}`}
                  />
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-neutral-500 whitespace-nowrap">Frais CHF</span>
                  <Input
                    type="number" step="0.5"
                    value={p.delivery_fee}
                    onChange={(e) => updatePostal(idx, { delivery_fee: e.target.value })}
                    className="h-9"
                    data-testid={`postal-fee-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removePostal(idx)}
                  data-testid={`remove-postal-${idx}`}
                  className="text-neutral-500 hover:text-[#D90429] p-2 rounded-md justify-self-end"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ---- IMPRIMANTE THERMIQUE (Star WebPRNT) ---- */}
        <div className="border-t border-neutral-200 pt-6">
          <div className="flex items-center gap-2 mb-3">
            <Printer className="w-4 h-4 text-neutral-700" />
            <Label className="text-xs uppercase tracking-wider font-bold">Imprimante thermique</Label>
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            Star TSP100IIILAN ou compatible WebPRNT. La tablette doit être sur le même Wi-Fi que l&apos;imprimante.
            <br />
            Si vous êtes en HTTPS et l&apos;impression échoue, autorisez le &quot;contenu non sécurisé&quot; pour ce site dans votre navigateur (Chrome : 🔒 → Paramètres du site).
          </p>

          <div className="flex items-center justify-between p-4 bg-neutral-50 rounded-md mb-3">
            <div>
              <div className="font-bold text-sm">Impression directe activée</div>
              <p className="text-xs text-neutral-500">Envoie le ticket directement à l&apos;imprimante, sans dialogue d&apos;impression.</p>
            </div>
            <Switch
              data-testid="printer-enabled-switch"
              checked={!!form.printer_enabled}
              onCheckedChange={(v) => setForm({ ...form, printer_enabled: v })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold">Adresse IP de l&apos;imprimante</Label>
              <Input
                data-testid="printer-ip-input"
                value={form.printer_ip}
                onChange={(e) => setForm({ ...form, printer_ip: e.target.value })}
                placeholder="192.168.1.14"
                className="h-10"
              />
            </div>
            <Button
              type="button"
              onClick={testPrint}
              disabled={testing || !form.printer_ip}
              data-testid="printer-test-btn"
              className="bg-[#2A9D8F] hover:bg-[#258377] text-white rounded-md h-10 px-4 text-sm font-bold whitespace-nowrap"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-3.5 h-3.5 mr-1" /> Test impression</>}
            </Button>
          </div>

          <div className="flex items-center gap-3 mt-3">
            <Checkbox
              id="printer-auto-cut"
              data-testid="printer-auto-cut"
              checked={!!form.printer_auto_cut}
              onCheckedChange={(v) => setForm({ ...form, printer_auto_cut: !!v })}
            />
            <label htmlFor="printer-auto-cut" className="text-sm cursor-pointer">
              Couper le papier automatiquement à la fin du ticket
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn" className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-11 px-6 font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </div>
      </div>

      {/* ---- MARKETING OPT-IN EMAILS ---- */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mt-6" data-testid="marketing-emails-panel">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-neutral-700" />
            <div>
              <h3 className="text-base font-black tracking-tight">Emails marketing (opt-in)</h3>
              <p className="text-xs text-neutral-500">
                Clients ayant accepté de recevoir les offres promotionnelles au checkout.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadMarketing}
            disabled={marketingLoading}
            data-testid="marketing-refresh-btn"
            className="text-neutral-500 hover:text-neutral-800 p-2 rounded-md disabled:opacity-50"
            title="Rafraîchir"
          >
            {marketingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div
            data-testid="marketing-count-badge"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A9D8F]/10 text-[#1f7a70] text-xs font-bold"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A9D8F]" />
            {marketingSubs.length} abonné(s)
          </div>
          <Button
            type="button"
            onClick={copyAllEmails}
            disabled={marketingSubs.length === 0}
            data-testid="marketing-copy-btn"
            variant="outline"
            className="h-9 px-3 text-xs font-bold rounded-md"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copier les emails
          </Button>
          <Button
            type="button"
            onClick={downloadCsv}
            disabled={marketingSubs.length === 0}
            data-testid="marketing-csv-btn"
            variant="outline"
            className="h-9 px-3 text-xs font-bold rounded-md"
          >
            Exporter CSV
          </Button>
          {marketingSubs.length > 0 && (
            <Button
              type="button"
              onClick={() => setMarketingExpanded((v) => !v)}
              data-testid="marketing-expand-btn"
              variant="ghost"
              className="h-9 px-3 text-xs font-bold rounded-md text-neutral-600"
            >
              {marketingExpanded ? (
                <><ChevronUp className="w-3.5 h-3.5 mr-1" /> Réduire</>
              ) : (
                <><ChevronDown className="w-3.5 h-3.5 mr-1" /> Voir la liste</>
              )}
            </Button>
          )}
        </div>

        {marketingSubs.length === 0 ? (
          <p
            data-testid="marketing-empty"
            className="text-xs text-neutral-500 italic border border-dashed border-neutral-300 rounded-md p-4 text-center"
          >
            Aucun client n&apos;a encore accepté de recevoir les offres marketing.
          </p>
        ) : (
          marketingExpanded && (
            <div
              data-testid="marketing-list"
              className="border border-neutral-200 rounded-md max-h-80 overflow-y-auto divide-y divide-neutral-100"
            >
              {marketingSubs.map((s, i) => (
                <div
                  key={(s.email || "") + i}
                  data-testid={`marketing-row-${i}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-3 py-2 text-sm hover:bg-neutral-50"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-neutral-900 truncate">{s.email || "—"}</div>
                    {(s.first_name || s.last_name) && (
                      <div className="text-xs text-neutral-500 truncate">
                        {[s.first_name, s.last_name].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-neutral-500 shrink-0">
                    {typeof s.total_orders === "number" && (
                      <span title="Commandes" className="inline-flex items-center gap-1">
                        <Package className="w-3 h-3" /> {s.total_orders}
                      </span>
                    )}
                    {s.marketing_opt_in_at && (
                      <span title="Opt-in" className="inline-flex items-center gap-1">
                        <CalendarClock className="w-3 h-3" /> {fmtDate(s.marketing_opt_in_at)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

// ---------- ACCOUNTING (Chiffres) PANEL ----------
const MONTHS_FR = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];

const AccountingPanel = () => {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const yearOptions = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, [now]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const { data } = await api.get(`/admin/accounting/summary`, { params: { year, month } });
      setSummary(data);
    } catch (e) {
      toast.error("Erreur lors du chargement des chiffres");
    } finally {
      setLoadingSummary(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/admin/accounting/pdf`, {
        params: { year, month },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quang-chiffres-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé");
    } catch (e) {
      toast.error("Erreur de génération du PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div data-testid="accounting-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 className="text-2xl font-black tracking-tight">Chiffres — Comptabilité</h2>
          <p className="text-sm text-neutral-500">
            Chiffre d&apos;affaires net, TVA collectée et produits vendus. Commandes supprimées ou annulées exclues.
          </p>
        </div>
      </div>

      {/* Period selector */}
      <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-5 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Mois</Label>
          <select
            data-testid="accounting-month"
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="mt-1 block w-40 h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm"
          >
            {MONTHS_FR.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">Année</Label>
          <select
            data-testid="accounting-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="mt-1 block w-32 h-10 rounded-md border border-neutral-300 bg-white px-3 text-sm"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        <Button
          onClick={downloadPdf}
          disabled={downloading || loadingSummary || !summary || summary.orders_count === 0}
          data-testid="download-accounting-pdf"
          className="bg-[#D90429] hover:bg-[#BF0424] text-white rounded-md h-10 px-5 font-bold"
        >
          {downloading ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Génération…</>
          ) : (
            <><Download className="w-4 h-4 mr-1.5" /> Télécharger PDF</>
          )}
        </Button>
      </div>

      {loadingSummary ? (
        <div className="flex items-center justify-center py-16 bg-white border border-neutral-200 rounded-lg">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : !summary ? null : summary.orders_count === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-12 text-center text-neutral-500" data-testid="accounting-empty">
          Aucune commande sur {summary.month_label}.
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <KpiTile label="CA Net (HT)" value={fmtCHF(summary.total_ht)} accent="#0A0A0A" testid="kpi-ht" />
            <KpiTile label="CA Brut (TTC)" value={fmtCHF(summary.total_ttc)} accent="#666" testid="kpi-ttc" />
            <KpiTile label="TVA collectée" value={fmtCHF(summary.total_vat)} accent="#D90429" testid="kpi-vat" />
            <KpiTile label="Commandes" value={String(summary.orders_count)} accent="#E9C46A" testid="kpi-orders" />
          </div>

          {/* VAT breakdown */}
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden mb-5">
            <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <h3 className="font-black text-sm tracking-tight">Détail de la TVA</h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">Les prix affichés au menu sont TTC (TVA incluse).</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vat-breakdown-table">
                <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Taux</th>
                    <th className="text-right px-4 py-2 font-bold">Cmd</th>
                    <th className="text-right px-4 py-2 font-bold">Base HT</th>
                    <th className="text-right px-4 py-2 font-bold">TVA</th>
                    <th className="text-right px-4 py-2 font-bold">TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.vat_breakdown.map((r) => (
                    <tr key={r.rate} className="border-t border-neutral-100">
                      <td className="px-4 py-2 font-bold">{r.rate.toFixed(1)}%</td>
                      <td className="px-4 py-2 text-right tabular-nums">{r.count}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtCHF(r.ht)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-[#D90429] font-bold">{fmtCHF(r.vat)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtCHF(r.ttc)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-neutral-900 bg-neutral-50 font-black">
                    <td className="px-4 py-2">TOTAL</td>
                    <td className="px-4 py-2 text-right tabular-nums">{summary.orders_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtCHF(summary.total_ht)}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-[#D90429]">{fmtCHF(summary.total_vat)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtCHF(summary.total_ttc)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <h3 className="font-black text-sm tracking-tight">Produits vendus ({summary.products.length})</h3>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="products-sold-table">
                <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Produit</th>
                    <th className="text-right px-4 py-2 font-bold">Qté</th>
                    <th className="text-right px-4 py-2 font-bold">CA</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.products.map((p, i) => (
                    <tr key={i} className="border-t border-neutral-100">
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-bold">{p.quantity}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtCHF(p.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const KpiTile = ({ label, value, accent, testid }) => (
  <div data-testid={testid} className="relative bg-white border border-neutral-200 rounded-lg p-4 overflow-hidden">
    <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: accent }} />
    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">{label}</p>
    <p className="text-lg md:text-xl font-black tracking-tight mt-2" style={{ color: accent }}>{value}</p>
  </div>
);
